/**
 * Player movement tuning. Design values only – derived physics values
 * (gravity, launch velocities) are computed in physics/JumpPhysics.ts.
 */
export type EasingName = 'linear' | 'easeOutQuad' | 'easeOutCubic' | 'easeInOutSine';

export interface PlayerColliderConfig {
  readonly width: number;
  readonly depth: number;
  readonly standingHeight: number;
  readonly slidingHeight: number;
}

export interface PlayerConfig {
  /** Seconds for a lane change across one full lane width. */
  readonly laneChangeDuration: number;
  /** Lane changes shorter than one lane width scale down, but never below this fraction. */
  readonly laneChangeMinDurationFactor: number;
  readonly laneChangeEasing: EasingName;
  /**
   * A turn taken on the spot (decided after the last exit lane line, or the rescue after a
   * missed turn) leaves the runner up to ≈ 0.4 m beside a lane, against its old running
   * direction. It drifts into the lane over this time (s) instead of jerking back.
   */
  readonly turnSettleDuration: number;

  /** Apex height of a ground jump (meters above take-off). */
  readonly jumpHeight: number;
  /** Ground-to-ground duration of a ground jump on flat ground (seconds). */
  readonly jumpDuration: number;
  /** Height gained by the double jump, measured from where it is triggered. */
  readonly doubleJumpHeight: number;
  /** Number of additional jumps allowed per airborne phase. */
  readonly maxAirJumps: number;
  /** Downward speed applied when "slide" is pressed in mid-air (m/s). */
  readonly fastFallSpeed: number;
  /** Terminal falling speed (m/s). */
  readonly maxFallSpeed: number;

  readonly slideDuration: number;
  readonly coyoteTime: number;
  readonly inputBufferTime: number;

  /**
   * Highest height difference the runner walks up or down without jumping (m).
   * A higher step in the way is a wall: running into it is a frontal hit, never a teleport onto it.
   */
  readonly maxStepHeight: number;
  /** Below the rim of a pit by this much, the runner has fallen in (costs a life or ends the run). */
  readonly pitFallDepth: number;
  /** Invulnerable runner meeting a wall hops over it automatically – with this much room to spare (m). */
  readonly vaultClearance: number;

  readonly landingDuration: number;
  readonly stumbleDuration: number;
  readonly hitDuration: number;
  /** Backwards push applied on a frontal hit (meters). */
  readonly hitPushBack: number;
  readonly invulnerabilityDuration: number;
  /** Delay between death and the game-over screen (seconds). */
  readonly deathToGameOverDelay: number;

  readonly startLane: number;
  readonly collider: PlayerColliderConfig;
}

export const PLAYER_CONFIG: PlayerConfig = {
  laneChangeDuration: 0.24,
  laneChangeMinDurationFactor: 0.4,
  laneChangeEasing: 'easeOutCubic',
  turnSettleDuration: 0.35,

  jumpHeight: 1.5,
  jumpDuration: 0.75,
  doubleJumpHeight: 1.5,
  maxAirJumps: 1,
  fastFallSpeed: 18,
  maxFallSpeed: 30,

  slideDuration: 0.8,
  coyoteTime: 0.1,
  inputBufferTime: 0.12,

  maxStepHeight: 0.3,
  pitFallDepth: 1,
  vaultClearance: 0.25,

  landingDuration: 0.08,
  stumbleDuration: 0.5,
  hitDuration: 44 / 30, // ANIM_Hit / ANIM_HitTrip: fall and get up again (HIT_FRAMES 45 at 30 fps, unit test)
  hitPushBack: 0.6,
  invulnerabilityDuration: 2,
  deathToGameOverDelay: 1.2,

  startLane: 1,
  collider: {
    width: 0.7,
    depth: 0.6,
    standingHeight: 1.8,
    slidingHeight: 0.9,
  },
};
